/**
 * THE HOVER PILL. A port of voice2me's orange-and-white call button, to the number.
 *
 * The reference is `InteractiveHoverButton` + `ActiveButton` in that project's
 * `components/call/button.tsx`, and this reproduces both halves of it:
 *
 *   IDLE — a 176 × 48 white pill, fully rounded, with a 2 px #363576 border and a
 *   wide soft drop shadow. Inside, an 8 px orange dot and the label at 18 px
 *   semibold, left-aligned behind 24 px of padding.
 *
 *   HOVER — the dot SCALES UP A HUNDRED-FOLD and becomes the button's fill. The
 *   label slides 48 px right and fades out; a second layer slides in from 48 px
 *   right to 20 px left, carrying the hover text and a microphone in white. Three
 *   simultaneous 300 ms transitions, all of them transform and opacity.
 *
 *   LIVE — the pill is orange, and holds a white stop disc and two white audio
 *   meters. Clicking it ends the call.
 *
 * THE DOT'S SCALE FACTOR IS 100.8 AND THAT IS THE REFERENCE'S OWN NUMBER
 * (`group-hover:scale-[100.8]`). An 8 px disc at 100.8× is 806 px across, which
 * covers a 176 × 48 pill from any point inside it with room to spare — the pill
 * clips it. Keeping the number rather than computing a tighter one matters: the
 * radius is what the eye reads during the 300 ms, and a smaller circle arrives as a
 * visible arc instead of as a wipe.
 *
 * ---------------------------------------------------------------------------
 * THREE THINGS THIS DOES NOT PORT, AND WHY
 *
 * 1. NO VIDEO BUTTONS. The reference's live state can grow camera and screen-share
 *    controls beside the pill. There is no video on this platform's call path, so
 *    there is nothing to draw.
 *
 * 2. NO "ARE YOU SURE?" DIALOG. That belongs to the reference's chat mode, where
 *    closing the window would also discard a conversation. This variant has no
 *    window and nothing to discard — see the chat flag for the one that does.
 *
 * 3. THE ERROR STATE IS INVENTED, because the reference has none: its button is
 *    simply `disabled` while connecting and has no failure rendering at all. A
 *    widget on a stranger's page must say when the microphone was blocked, so the
 *    pill widens and turns red with the sentence in it — the flag's approach, since
 *    the flag is the widget that already had to solve this.
 *
 * ---------------------------------------------------------------------------
 * THREE THINGS THIS CHANGES ON PURPOSE (2026-09-04, the Gene public page)
 *
 * 1. THE IDLE ROW IS CENTRED AS A GROUP. The reference anchors the dot 24 px from
 *    the left and lets the label follow, which is right for its own 176 px pill and
 *    its own two-word label — the group happens to land in the middle. Give the
 *    widget a wider pill or a shorter word and the same rule parks everything on the
 *    left with a hole on the right, which is what the first Gene screenshot showed.
 *    So the label is MEASURED (a ResizeObserver on the span; fonts load late) and
 *    the dot is placed so that dot + gap + label sit centred, with a 12 px shoulder
 *    as the floor. For the reference's own label and width the arithmetic returns
 *    the reference's 24 px, so the port is unchanged where it was a port. The fill
 *    still sweeps from wherever the dot actually is.
 *
 * 2. A MUTE CONTROL WHILE LIVE. The reference has none; the flag, the chat flag and
 *    the conversation bar all do, and a visitor who has muted on one of them looks
 *    for it here. It is the flag's object exactly — a 24 px disc in `onAccent`,
 *    hollow while the microphone is open, filled while muted — at the right-hand end
 *    of the live row, and the meters give up its width. It cannot be INSIDE the pill:
 *    the pill is one `<button>`, and a button inside a button is not HTML. It is a
 *    sibling over the pill, in a wrapper that exists only to position it.
 *    `allowMute={false}` removes it and gives the meters the room back.
 *
 * 3. THE METER BAR COUNT IS A PROP. Five stays the default — the product decision
 *    at `METER_BARS` still holds — but a wider pill has room for more, and Gene's
 *    asked for seven. Clamped to 3–12; FlagBars scales the bar widths to fill the
 *    same proportion of the box whatever the count.
 *
 * ---------------------------------------------------------------------------
 * THE CLICK IS PORTED, AND IT IS A SWITCH. The reference plays a 46 ms WAV on
 * `mouseenter`; so does this, behind `sound`, defaulting on because that is what the
 * reference does. The first cut left it out on two grounds and one of them was
 * simply wrong — see the header of `hover-pill-sound.ts` for the measurements. The
 * ground that stands is that unrequested audio on a stranger's page is a decision
 * for whoever deploys the widget, which is exactly what a switch makes it.
 *
 * `play()` is allowed to fail and is ignored when it does: a browser that has not
 * seen a user gesture yet refuses to start audio, and a pointer crossing a button is
 * not a gesture. That is the reference's behaviour too, and the right one — a widget
 * whose console fills with rejected promises because somebody moved a mouse is worse
 * than a widget that is quiet until first click.
 *
 * ---------------------------------------------------------------------------
 * NO STYLESHEET, like the two flags and the conversation bar. There is not one class
 * name in this file: it renders inside a closed shadow root on a page that is not
 * ours, where `:root` tokens and compiled utilities do not reach and the embed's own
 * stylesheet can be blocked outright. The reference is Tailwind classes throughout,
 * so every one of them has been resolved to its value here — `w-44` is 176, `h-12`
 * is 48, `text-lg` is 18 px, `bg-orange-500` is #f97316, `p-2 px-6` is 8 px and
 * 24 px, `translate-x-12` is 48 px and `-translate-x-5` is −20. Font sizes are px
 * and never rem, because rem resolves against the HOST page's `<html>` even inside
 * a shadow root.
 */
import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { FlagBars } from './flag-bars'
import { HOVER_CLICK_WAV } from './hover-pill-sound'
import { isLive, type VoiceControl, type VoicePhase, type VoicePlacement, type VoiceWidgetCommon } from './types'
import { withTakeover } from './stage-text'
import { MicOffGlyph } from './mic-glyphs'

/** Inline for the file's usual reason — see the header. */
const FONT_STACK =
  "system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif"

/** `bg-orange-500`, `text-primary-foreground`, `border-[#363576]`, `bg-background`. */
const ACCENT = '#f97316'
const ON_ACCENT = '#ffffff'
const SURFACE = '#ffffff'
const ON_SURFACE = '#0a0a0a'
const BORDER = '#363576'

/** The reference has no failure state; this is the flag's red. See the header. */
const ERROR_BG = '#fb2c36'
const ERROR_FG = '#ffffff'

/** `w-44 h-12`, and the width the error sentence needs instead. */
const W = 176
const H = 48
const ERROR_W = 260

/** `border-2`, `px-6`, `p-2`. */
const BORDER_W = 2
const PAD_X = 24
/** The live row's own padding — the reference's `px-4` — because a stop disc wants
 *  less shoulder than a word does. */
const LIVE_PAD_X = 16
/** The reference's `gap-4` between the stop disc and the meters, and `gap-2`
 *  between the dot and the label. */
const LIVE_GAP = 16
const IDLE_GAP = 8

/** `h-2 w-2`, and `group-hover:scale-[100.8]`. See the header for the factor. */
const DOT = 8
const DOT_SCALE = 100.8

/**
 * `translate-x-12` — where the hover layer starts, off to the right, before it
 * slides in.
 *
 * THERE IS NO `-translate-x-5` HERE AND UPSTREAM HAS ONE. Its hover row is centred
 * and then nudged 20 px left, which is tuned for "Start Chat" beside a microphone
 * and leaves about nine pixels of shoulder; any longer word runs under the pill's
 * edge, and `hoverLabel` is a field an operator sets. Landing at 0 centres the row
 * properly at every string length, and the slide-in it is the tail of is the part
 * of that gesture anybody sees.
 */
const SLIDE_OUT = 48

/** Every one of the reference's transitions is `duration-300`. */
const MS = 300

/** `shadow-[0_12px_36px_rgb(0,0,0,0.26)]`, which is on the button in both states. */
const SHADOW = '0 12px 36px rgba(0, 0, 0, 0.26)'

/**
 * FIVE BARS A SIDE. The reference's `barWidth={15} barSpacing={8}` in a ~56 px meter
 * works out at TWO, which is a thick pair that barely reads as a meter at all —
 * there is not enough of it to tell a vowel from a consonant. Five is the count that
 * still looks like the reference's chunky bars and actually moves; product decision
 * 2026-08-25. `FlagBars` takes the count and derives the widths.
 */
const METER_BARS = 5
/** The mute disc — the flag's size, so the two widgets share one object. */
const MUTE_SIZE = 24
/** The smallest shoulder the centred idle group may leave on either side. */
const MIN_SHOULDER = 12
/** `h-6` on the meter row, and `gap-1` between the two meters. */
const METER_H = 24
const METER_GAP = 4

/** Same reasoning as the flag's: high enough to win on a stranger's page, with
 *  headroom left above it. See the note in flag-button.tsx. */
const DEFAULT_Z = 2147483000

/**
 * `theme` is omitted, the refusal the flag and the conversation bar both make: the
 * light/dark switch paints by putting a class on a wrapper for `embed.css`'s tokens
 * to key off, and there is not one class name in this file. `surface`/`onSurface`/
 * `border` are this widget's theme, and `accent`/`onAccent` are its livery.
 *
 * `endLabel` is omitted too, and for the reason the config editor gives for the
 * orb: the live pill is a stop disc and two meters, so there is nowhere to paint a
 * word. What that string would have reached is the `aria-label`, which `hoverLabel`
 * already says better.
 */
export interface HoverPillProps extends Omit<VoiceWidgetCommon, 'theme' | 'endLabel'> {
  control: VoiceControl
  /** The word revealed by the hover wipe, over the orange. The reference's
   *  "Start Chat" to `label`'s "Agent Ready". */
  hoverLabel?: string
  /** 'inline' (default) renders in flow; anything else fixes it to that corner. */
  placement?: VoicePlacement
  width?: number
  height?: number
  surface?: string
  onSurface?: string
  /** The 2 px ring. The reference's indigo against its white pill — a third colour,
   *  and the one that makes this button look like itself. */
  border?: string
  /** Play the reference's click when the pointer arrives. On by default, because
   *  that is what the reference does. See the header. */
  sound?: boolean
  /** The mute toggle at the right of the live row. Default true. */
  allowMute?: boolean
  /** Bars per meter while live. Default 5, the reference's; clamped to 3–12. */
  meterBars?: number
  zIndex?: number
}

export function HoverPill({
  control,
  placement = 'inline',
  width = W,
  height = H,
  zIndex = DEFAULT_Z,
  label = 'Agent Ready',
  hoverLabel = 'Start Chat',
  accent = ACCENT,
  onAccent = ON_ACCENT,
  surface = SURFACE,
  onSurface = ON_SURFACE,
  border = BORDER,
  sound = true,
  caption,
  allowMute = true,
  meterBars = METER_BARS,
}: HoverPillProps) {
  const [hover, setHover] = useState(false)
  const [ring, setRing] = useState(false)
  const [muteHover, setMuteHover] = useState(false)
  const [muteRing, setMuteRing] = useState(false)
  const bars = Math.max(3, Math.min(12, Math.round(meterBars)))

  /**
   * Where the dot goes, so that dot + gap + label sit centred (header, change 1).
   * `null` until the label has been measured; the reference's 24 px until then, so
   * the first paint is the port and the centring is a correction to it rather than
   * a jump from nowhere. Measured with a ResizeObserver because the label's width
   * changes when the host page's fonts finish loading, not only on mount.
   */
  const labelRef = useRef<HTMLSpanElement>(null)
  const [labelW, setLabelW] = useState<number | null>(null)
  useEffect(() => {
    const el = labelRef.current
    if (el === null) return
    const measure = () => setLabelW(el.getBoundingClientRect().width)
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [label, width])
  const dotLeft =
    labelW === null ? PAD_X : Math.max(MIN_SHOULDER, Math.round((width - (DOT + IDLE_GAP + labelW)) / 2))
  const muted = control.muted
  /** Room the live row keeps clear at its right end for the mute disc. */
  const muteRoom = allowMute ? MUTE_SIZE + LIVE_GAP : 0
  const still = useMediaQuery('(prefers-reduced-motion: reduce)')
  const click = useClick(sound)

  const phase = control.phase
  const live = isLive(phase)
  const failed = phase === 'error'
  /** Connecting is the only stretch a click must wait through; the hang-up's own
   *  teardown is not rendered at all (see `VoicePhase`). */
  const busy = phase === 'connecting'

  /**
   * THE WIPE PLAYS FOR CONNECTING TOO, and that is a deliberate addition rather
   * than a port. The reference simply `disabled`s its button between the click and
   * the connection — so the two most uncertain seconds of the interaction look
   * identical to not having clicked. Holding the orange fill and swapping the word
   * to "Connecting…" costs nothing and answers the only question a visitor has.
   */
  const filled = live || busy || (hover && !failed)

  function onClick() {
    // Idempotent on the session's side, but a button that still looks pressable
    // mid-connect invites the double click in the first place. Stated cost: a
    // connect that hangs cannot be cancelled from here until it fails on its own,
    // which is the recoverable half of that trade — the flag's reasoning.
    if (busy) return
    if (live) control.stop()
    else control.start()
  }

  const bg = failed ? ERROR_BG : filled ? accent : surface
  const fg = failed ? ERROR_FG : filled ? onAccent : onSurface
  /** Two tones, so the focus ring reads whether the page behind it is light or
   *  dark. Appended to the drop shadow because a box has only one of these. */
  const ringShadow = ring ? `, 0 0 0 2px ${fg}, 0 0 0 5px ${bg}` : ''

  const button: CSSProperties = {
    // No reset exists inside the shadow root, so everything a UA stylesheet would
    // have an opinion about is stated.
    boxSizing: 'border-box',
    appearance: 'none',
    margin: 0,
    padding: 0,
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: live || failed ? 'flex-start' : 'center',
    width: failed ? ERROR_W : width,
    height,
    // `rounded-full`: half the height, so it stays a capsule at any size.
    borderRadius: height / 2,
    border: `${BORDER_W}px solid ${failed ? ERROR_BG : border}`,
    background: bg,
    color: fg,
    boxShadow: SHADOW + ringShadow,
    // Not the `font` shorthand: a shorthand followed by longhands depends on the
    // order React happens to assign them in. px, never rem — see the header.
    fontFamily: FONT_STACK,
    fontWeight: 600,
    fontSize: '18px',
    lineHeight: 1,
    letterSpacing: 'normal',
    textTransform: 'none',
    textAlign: 'center',
    cursor: busy ? 'progress' : 'pointer',
    outline: 'none',
    touchAction: 'manipulation',
    WebkitTapHighlightColor: 'transparent',
    transition: still
      ? 'none'
      : [
          `width ${MS}ms ease`,
          `background-color ${MS}ms ease`,
          `border-color ${MS}ms ease`,
          `color ${MS}ms ease`,
          `box-shadow ${MS}ms ease`,
        ].join(', '),
  }

  const body = (
    <span
      style={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 6,
        fontFamily: FONT_STACK,
      }}
    >
      <span style={{ position: 'relative', display: 'inline-block', lineHeight: 0 }}>
      <button
        type="button"
        onClick={onClick}
        aria-label={ariaLabel(phase, label, hoverLabel)}
        aria-disabled={busy || undefined}
        // `:focus-visible` needs a stylesheet; asking the element whether it
        // matches does not, and gives the same answer.
        onFocus={(e) => {
          setRing(e.currentTarget.matches(':focus-visible'))
          // Keyboard focus should reveal what hover reveals, or the hover text is
          // a thing only a mouse can read.
          setHover(true)
        }}
        onBlur={() => {
          setRing(false)
          setHover(false)
        }}
        onPointerEnter={() => {
          setHover(true)
          // Only when the pointer ARRIVES, never on focus: a keyboard user tabbing
          // through a page would otherwise hear it, and they did not aim at this.
          click()
        }}
        onPointerLeave={() => setHover(false)}
        style={button}
      >
        {/*
          THE DOT, WHICH IS ALSO THE FILL. One element doing both jobs is the
          reference's trick and it is worth keeping: a separate background
          transition would cross-fade the colour, while a scaling disc sweeps it in
          from where the dot actually was, so the fill has an origin.

          It sits at the dot's resting position rather than at the centre, which is
          why the wipe reads as coming from the left. `borderRadius: '50%'` on a
          scaled element stays a circle; scaling a rounded rectangle would not.
        */}
        <span
          aria-hidden
          style={{
            position: 'absolute',
            top: '50%',
            left: dotLeft,
            width: DOT,
            height: DOT,
            marginTop: -DOT / 2,
            borderRadius: '50%',
            background: failed ? ERROR_FG : accent,
            // Failure paints the whole pill red, so the dot has nothing to fill and
            // stays a dot — a red disc sweeping over red says nothing.
            transform: filled && !failed ? `scale(${DOT_SCALE})` : 'scale(1)',
            transition: still ? 'none' : `transform ${MS}ms ease`,
            pointerEvents: 'none',
          }}
        />

        {/* Idle: the label, behind the dot's own width. Slides right and fades as
            the fill arrives — all three transitions run together, which is what
            makes one gesture out of them. */}
        <Layer
          visible={!live && !failed}
          still={still}
          style={{
            justifyContent: 'flex-start',
            paddingLeft: dotLeft + DOT + IDLE_GAP,
            paddingRight: MIN_SHOULDER,
            opacity: filled ? 0 : 1,
            transform: `translateX(${filled ? SLIDE_OUT : 0}px)`,
          }}
        >
          <span ref={labelRef} style={{ whiteSpace: 'nowrap' }}>{label}</span>
        </Layer>

        {/*
          Hover: the word and the microphone, in the ink that reads on the fill.

          `&& !live` ON THE OPACITY IS NOT REDUNDANT WITH `visible`, and leaving it
          out was a real defect. `visibility` is discrete, so it flips at the END of
          the 300 ms when going away — which is exactly the point of putting it in
          the transition list — and a layer whose opacity is still 1 for that whole
          stretch is PAINTED over the layer fading in underneath it. Connecting a
          call showed "Start Chat" sitting on top of the stop control and the meters
          for a third of a second, then cutting. The two have to cross-fade.
        */}
        <Layer
          visible={!live && !failed}
          still={still}
          style={{
            justifyContent: 'center',
            gap: IDLE_GAP,
            // Shoulders, so a long `hoverLabel` runs out of room before it runs off
            // the pill: without them the content is free to leave the box, and the
            // shift below is what pushes it there.
            paddingLeft: 12,
            paddingRight: 12,
            color: onAccent,
            opacity: filled && !live ? 1 : 0,
            // Slides in from the right and stops centred — see `SLIDE_OUT`.
            transform: `translateX(${filled ? 0 : SLIDE_OUT}px)`,
          }}
        >
          <span
            style={{
              minWidth: 0,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {busy ? 'Connecting…' : hoverLabel}
          </span>
          <MicGlyph size={22} />
        </Layer>

        {/* Live: the stop disc and the two meters, both in `onAccent`. */}
        <Layer
          visible={live}
          still={still}
          style={{
            justifyContent: 'flex-start',
            gap: LIVE_GAP,
            paddingLeft: LIVE_PAD_X,
            paddingRight: LIVE_PAD_X + muteRoom,
            color: onAccent,
            opacity: live ? 1 : 0,
          }}
        >
          <StopGlyph size={24} />
          {/*
            `flex: 1` rather than the reference's fixed `w-28`. Its 112 px meter box
            plus a 24 px disc plus a 16 px gap is 152 inside a 140 px content area,
            so upstream overflows its own pill and relies on `overflow-hidden` to
            hide the fact. Letting the meters take what is left is the same picture
            without the clipping, and it survives a customer who sets `width`.
          */}
          <span style={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'flex-start' }}>
            <FlagBars
              control={control}
              active={live}
              color={onAccent}
              // The user's meter first — the reference renders `isUser` first.
              reverse
              meterWidth={Math.max(24, (width - LIVE_PAD_X * 2 - 24 - LIVE_GAP - METER_GAP - muteRoom) / 2)}
              meterHeight={METER_H}
              gap={METER_GAP}
              bars={bars}
            />
          </span>
        </Layer>

        {/* Failure. Invented — see the header — and the pill widens for it. */}
        <Layer
          visible={failed}
          still={still}
          style={{
            justifyContent: 'flex-start',
            gap: 10,
            paddingLeft: LIVE_PAD_X,
            paddingRight: LIVE_PAD_X,
            color: ERROR_FG,
            opacity: failed ? 1 : 0,
          }}
        >
          <WarningGlyph size={22} />
          <span
            style={{
              fontSize: '12px',
              fontWeight: 500,
              lineHeight: 1.25,
              textAlign: 'left',
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 3,
              overflow: 'hidden',
            }}
          >
            {control.error ?? 'The call could not be connected. Please try again.'}
          </span>
        </Layer>
      </button>
      {/*
        THE MUTE DISC — header, change 2. A sibling of the pill, not a child: the
        pill is a button. Positioned over the live row's right end, where the row
        left `muteRoom` clear for it. Always in the tree when `allowMute` is set and
        faded rather than mounted, so going live does not pop a control into
        existence; not focusable and not hit-testable until then.
      */}
      {allowMute && (
        <button
          type="button"
          onClick={control.toggleMute}
          tabIndex={live ? 0 : -1}
          aria-hidden={!live}
          title={muted ? 'Unmute your microphone' : 'Mute your microphone'}
          aria-label={muted ? 'Unmute your microphone' : 'Mute your microphone'}
          aria-pressed={muted}
          onFocus={(e) => setMuteRing(e.currentTarget.matches(':focus-visible'))}
          onBlur={() => setMuteRing(false)}
          onPointerEnter={() => setMuteHover(true)}
          onPointerLeave={() => setMuteHover(false)}
          style={{
            position: 'absolute',
            top: '50%',
            right: LIVE_PAD_X + BORDER_W,
            transform: 'translateY(-50%)',
            boxSizing: 'border-box',
            appearance: 'none',
            margin: 0,
            padding: 0,
            width: MUTE_SIZE,
            height: MUTE_SIZE,
            borderRadius: '50%',
            border: `1.5px solid ${onAccent}`,
            background: muted ? onAccent : 'transparent',
            color: muted ? accent : onAccent,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            outline: 'none',
            touchAction: 'manipulation',
            WebkitTapHighlightColor: 'transparent',
            lineHeight: 0,
            // Quieter than the stop disc beside it at rest; full strength when hovered,
            // and when it is the thing that is on — the flag's rule.
            opacity: live ? (muteHover || muted ? 1 : 0.8) : 0,
            pointerEvents: live ? 'auto' : 'none',
            boxShadow: muteRing ? `0 0 0 2px ${accent}, 0 0 0 4px ${onAccent}` : 'none',
            transition: still ? 'none' : `opacity ${MS}ms ease, background-color 150ms ease, color 150ms ease`,
          }}
        >
          {muted ? <MicOffGlyph size={14} /> : <MicGlyph size={14} />}
        </button>
      )}
      </span>

      {caption ? (
        <span
          style={{
            fontSize: '11.2px',
            fontWeight: 300,
            lineHeight: 1.2,
            color: onSurface,
            opacity: 0.7,
            maxWidth: failed ? ERROR_W : width,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {caption}
        </span>
      ) : null}

      {/* Outside the button, so the announcement is not entangled with its name,
          and visually hidden without a stylesheet. The coarse transitions only —
          see `statusSentence` on the flag for why the live phases stay silent. */}
      <span aria-live="polite" style={SR_ONLY}>
        {withTakeover(control, statusSentence(phase, control.error))}
      </span>
    </span>
  )

  if (placement === 'inline') return body
  return (
    <div style={{ position: 'fixed', ...CORNER[placement], zIndex }}>{body}</div>
  )
}

/** The four corners, 24 px in — the same inset the other floating variants use. */
const CORNER: Record<Exclude<VoicePlacement, 'inline'>, CSSProperties> = {
  'bottom-right': { bottom: 24, right: 24 },
  'bottom-left': { bottom: 24, left: 24 },
  'top-right': { top: 24, right: 24 },
  'top-left': { top: 24, left: 24 },
  left: { top: '50%', left: 24, transform: 'translateY(-50%)' },
  right: { top: '50%', right: 24, transform: 'translateY(-50%)' },
}

/**
 * One absolutely-positioned face of the pill.
 *
 * All four stay mounted and trade places on opacity and transform, which is the
 * reference's structure and the reason the wipe reads as one motion: swapping the
 * children out would restart layout on the frame the fill starts moving.
 *
 * `visibility` IS IN THE TRANSITION LIST AND THAT IS THE TRICK — it is a discrete
 * property, so it flips at the START of the transition when becoming visible and at
 * the END when becoming hidden. The fade still plays, and once it has, the hidden
 * layer stops being painted, so the live meters are not compositing behind an idle
 * button on every page this is installed on.
 */
function Layer({
  visible,
  still,
  style,
  children,
}: {
  visible: boolean
  still: boolean
  style: CSSProperties
  children: ReactNode
}) {
  return (
    <span
      aria-hidden
      style={{
        position: 'absolute',
        inset: 0,
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        visibility: visible ? 'visible' : 'hidden',
        pointerEvents: 'none',
        transition: still
          ? 'none'
          : `opacity ${MS}ms ease, transform ${MS}ms ease, visibility ${MS}ms ease`,
        ...style,
      }}
    >
      {children}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Glyphs. `currentColor` throughout, so each is white on the orange and the
// surface's ink on the white without being told which.
// ---------------------------------------------------------------------------

/** The reference's `CircleX`: a ring with a cross in it, drawn as strokes. */
function StopGlyph({ size }: { size: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden focusable="false" style={{ flexShrink: 0 }}>
      <circle cx="12" cy="12" r="9.25" stroke="currentColor" strokeWidth={1.8} fill="none" />
      <path
        d="M9 9l6 6M15 9l-6 6"
        stroke="currentColor"
        strokeWidth={1.8}
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  )
}

/** The reference's lucide `Mic`, drawn as strokes at its own proportions. */
function MicGlyph({ size }: { size: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden focusable="false" style={{ flexShrink: 0 }}>
      <rect x="9" y="2" width="6" height="11" rx="3" stroke="currentColor" strokeWidth={1.8} fill="none" />
      <path
        d="M19 10.5v1a7 7 0 0 1-14 0v-1M12 18.5V22M8.5 22h7"
        stroke="currentColor"
        strokeWidth={1.8}
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  )
}

function WarningGlyph({ size }: { size: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden focusable="false" style={{ flexShrink: 0 }}>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10.6 3.1a1.62 1.62 0 0 1 2.8 0l8.1 14.05a1.62 1.62 0 0 1-1.4 2.43H3.9a1.62 1.62 0 0 1-1.4-2.43L10.6 3.1ZM12 8.1a.85.85 0 0 0-.85.85v4a.85.85 0 0 0 1.7 0v-4A.85.85 0 0 0 12 8.1Zm0 6.8a.95.95 0 1 0 0 1.9.95.95 0 0 0 0-1.9Z"
      />
    </svg>
  )
}

// ---------------------------------------------------------------------------
// Wording, and the one hook that replaces CSS this file cannot have
// ---------------------------------------------------------------------------

/** The button's name says what a click will DO, which is not what the widget is
 *  currently doing — that is the live region's job. `hoverLabel` is the visible
 *  word for starting, so it is the name for starting. */
function ariaLabel(phase: VoicePhase, label: string, hoverLabel: string): string {
  if (isLive(phase)) return 'End the call'
  if (phase === 'connecting') return 'Connecting, please wait'
  if (phase === 'error') return `${label} — retry`
  return hoverLabel
}

/**
 * What the live region says, and what it refuses to say: `listening`, `thinking`
 * and `speaking` alternate several times a minute, and narrating them into a polite
 * region interrupts a screen-reader user mid-turn while they are trying to hear the
 * agent. Only the coarse transitions are announced. The flag's rule, and its reason.
 */
function statusSentence(phase: VoicePhase, error: string | null): string {
  switch (phase) {
    case 'idle':
      return ''
    case 'connecting':
      return 'Connecting.'
    case 'listening':
    case 'thinking':
    case 'speaking':
      return 'Connected. The call is running.'
    case 'ended':
      return 'Call ended.'
    case 'error':
      return error ?? 'The call could not be connected.'
  }
}

const SR_ONLY: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  margin: -1,
  padding: 0,
  border: 0,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  clipPath: 'inset(50%)',
}

/**
 * The click, as a function you may call as often as you like.
 *
 * ONE `Audio` ELEMENT, CREATED LAZILY AND REWOUND. Constructing a new one per hover
 * would decode 8 kB again each time and leave a trail of elements for the collector;
 * `currentTime = 0` restarts the same one, which is the reference's own approach and
 * is what makes a fast in-out-in sound like three clicks rather than one.
 *
 * LAZY BECAUSE OF THE SWITCH AND THE SERVER. `new Audio(...)` at module scope would
 * decode the blob on every page that loads the bundle, including the ones whose
 * operator turned this off, and would throw outright where there is no `window`.
 * Nothing is constructed until a pointer actually arrives at a button with `sound`
 * on.
 *
 * `catch` IS NOT SWALLOWING AN ERROR. Autoplay policy rejects `play()` until the
 * page has seen a real user gesture, and moving a pointer is not one — so the first
 * hover on a freshly loaded page is expected to be silent, every time. There is
 * nothing to report and nobody to report it to.
 */
function useClick(enabled: boolean): () => void {
  const el = useRef<HTMLAudioElement | null>(null)
  useEffect(() => {
    // Turning the switch off mid-life should drop the buffer, not just skip play.
    if (!enabled) el.current = null
  }, [enabled])
  return useCallback(() => {
    if (!enabled || typeof window === 'undefined') return
    el.current ??= new Audio(HOVER_CLICK_WAV)
    el.current.currentTime = 0
    void el.current.play().catch(() => {})
  }, [enabled])
}

/** `matchMedia`, not a Tailwind variant: inside the embed's shadow root there is no
 *  compiled stylesheet for a breakpoint or a motion query to live in. */
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window === 'undefined' ? false : window.matchMedia(query).matches,
  )
  useEffect(() => {
    if (typeof window === 'undefined') return
    const mq = window.matchMedia(query)
    setMatches(mq.matches)
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [query])
  return matches
}
